import React from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation.jsx';
import Footer from '../components/Footer.jsx';
import {
    Container,
    Text,
    Heading,
    Button,
    Icon
} from '../components/ui/index.jsx';

export default function CookiePolicy() {
    const navigate = useNavigate();
    const lastUpdated = 'January 4, 2026';
    const [showCookieDetails, setShowCookieDetails] = React.useState(false);

    return (
        <div className="min-h-screen text-white selection:bg-[#7B61FF] selection:text-white relative overflow-hidden">
            {/* Aurora Background */}
            <div className="fixed inset-0 aurora-bg z-0 pointer-events-none"></div>

            {/* Ambient Orbs */}
            <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
                <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-[#7B61FF]/20 rounded-full blur-[100px] animate-pulse-slow"></div>
                <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-[#9F7AEA]/20 rounded-full blur-[100px] animate-pulse-slow" style={{ animationDelay: '2s' }}></div>
            </div>

            {/* Content Wrapper */}
            <div className="relative z-10 flex flex-col min-h-screen">
                <Navigation />

                <main className="flex-1">
                    <Container size="4xl" className="py-12">
                        {/* Header */}
                        <div className="text-center mb-12 px-4">
                            <Heading level={1} className="mb-4 bg-clip-text text-transparent bg-gradient-to-b from-white to-white/50">
                                Cookie Policy
                            </Heading>
                            <Text variant="secondary" size="sm">
                                Last updated: {lastUpdated}
                            </Text>
                            <div className="w-24 h-1 bg-[#7B61FF] mx-auto rounded-full shadow-[0_0_10px_#7B61FF] mt-6"></div>
                        </div>

                        {/* Content */}
                        <div className="glass-panel p-8 md:p-12 rounded-2xl border-white/10 space-y-8">

                            {/* Introduction */}
                            <section>
                                <Heading level={3} className="mb-4 text-[#BA87FF]">1. What Are Cookies?</Heading>
                                <Text variant="secondary" className="leading-relaxed">
                                    Cookies are small text files that are placed on your device when you visit a website.
                                    They are widely used to make websites work more efficiently, provide information to
                                    website owners, and enhance user experience. This Cookie Policy explains how The Architect
                                    uses cookies and similar technologies.
                                </Text>
                            </section>

                            {/* Types of Cookies We Use */}
                            <section>
                                <Heading level={3} className="mb-4 text-[#BA87FF]">2. Categories of Cookies We Use</Heading>
                                <Text variant="secondary" className="leading-relaxed mb-4">
                                    We categorize cookies based on their purpose and legal basis. Under GDPR and the ePrivacy Directive,
                                    we are required to inform you about each category, its purpose, and whether consent is required.
                                </Text>

                                {/* Strictly Necessary Cookies */}
                                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-4">
                                    <Text variant="secondary" className="leading-relaxed mb-2">
                                        <strong className="text-green-400">Strictly Necessary Cookies</strong>
                                    </Text>
                                    <Text variant="secondary" className="leading-relaxed mb-3">
                                        <strong>Legal Basis:</strong> Legitimate interest (no consent required)
                                    </Text>
                                    <Text variant="secondary" className="leading-relaxed mb-3">
                                        These cookies are essential for the website to function properly. Without them, the service
                                        cannot operate. They include authentication and security cookies that keep you logged in
                                        and protect your account.
                                    </Text>

                                    {/* Collapsible Cookie Details */}
                                    <button
                                        onClick={() => setShowCookieDetails(!showCookieDetails)}
                                        className="flex items-center gap-2 text-[#7B61FF] hover:text-[#8A6DFF] text-sm font-medium mb-3 transition-colors"
                                    >
                                        <span>{showCookieDetails ? '▼' : '▶'}</span>
                                        View Cookie Details
                                    </button>

                                    {showCookieDetails && (
                                        <div className="overflow-x-auto mb-3">
                                            <table className="w-full text-sm text-left text-gray-300 border border-white/10 rounded-lg overflow-hidden">
                                                <thead className="bg-white/5 text-xs uppercase text-gray-400">
                                                    <tr>
                                                        <th className="px-4 py-2 border-b border-white/10">Cookie Name</th>
                                                        <th className="px-4 py-2 border-b border-white/10">Purpose</th>
                                                        <th className="px-4 py-2 border-b border-white/10">Duration</th>
                                                        <th className="px-4 py-2 border-b border-white/10">Type</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr className="border-b border-white/5">
                                                        <td className="px-4 py-2 font-mono text-xs text-green-400">architect_session</td>
                                                        <td className="px-4 py-2">Maintains your authenticated session after signing in</td>
                                                        <td className="px-4 py-2">30 days</td>
                                                        <td className="px-4 py-2">HttpOnly, Secure</td>
                                                    </tr>
                                                    <tr className="border-b border-white/5">
                                                        <td className="px-4 py-2 font-mono text-xs text-green-400">device_fingerprint</td>
                                                        <td className="px-4 py-2">Identifies your device for security, detecting unauthorized access</td>
                                                        <td className="px-4 py-2">30 days</td>
                                                        <td className="px-4 py-2">HttpOnly, Secure</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="px-4 py-2 font-mono text-xs text-green-400">device_fp</td>
                                                        <td className="px-4 py-2">Temporary cookie used during sign-in to link your device</td>
                                                        <td className="px-4 py-2">10 minutes</td>
                                                        <td className="px-4 py-2">Secure</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    )}

                                    <Text variant="secondary" className="leading-relaxed text-xs text-gray-400">
                                        You cannot opt out of strictly necessary cookies as they are required for the service to function.
                                    </Text>
                                </div>

                                {/* Local Storage */}
                                <div className="bg-[#7B61FF]/10 border border-[#7B61FF]/30 rounded-lg p-4">
                                    <Text variant="secondary" className="leading-relaxed mb-2">
                                        <strong className="text-[#BA87FF]">Browser Local Storage</strong>
                                    </Text>
                                    <Text variant="secondary" className="leading-relaxed mb-3">
                                        <strong>Legal Basis:</strong> Legitimate interest (essential for service operation)
                                    </Text>
                                    <Text variant="secondary" className="leading-relaxed mb-3">
                                        In addition to cookies, we use browser local storage for:
                                    </Text>
                                    <ul className="list-disc list-inside space-y-2 text-gray-300 text-sm">
                                        <li><strong>Cookie notice acknowledgment:</strong> Remembers that you have seen and
                                        acknowledged our cookie notice so we don't show it repeatedly.</li>
                                        <li><strong>Session data:</strong> Temporary data needed during your current analysis
                                        session. Cleared when you close your browser or start a new session.</li>
                                        <li><strong>UI state:</strong> Remembers UI preferences such as dismissed notifications
                                        to improve your experience.</li>
                                    </ul>
                                </div>
                            </section>

                            {/* Third-Party Cookies */}
                            <section>
                                <Heading level={3} className="mb-4 text-[#BA87FF]">3. Third-Party Services & Cookies</Heading>
                                <Text variant="secondary" className="leading-relaxed mb-4">
                                    We use third-party services that may place cookies on your device. Under GDPR Article 13,
                                    we must disclose these recipients of your data:
                                </Text>
                                <div className="space-y-4">
                                    <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                                        <Text variant="secondary" className="leading-relaxed mb-2">
                                            <strong className="text-white">Google (Authentication)</strong>
                                        </Text>
                                        <Text variant="secondary" className="leading-relaxed text-sm">
                                            <strong>Purpose:</strong> Google OAuth enables you to sign in using your Google account.<br/>
                                            <strong>Data collected:</strong> Email address, name, and profile picture from your Google account.<br/>
                                            <strong>Legal basis:</strong> Consent (you choose to sign in with Google).<br/>
                                            <strong>Privacy policy:</strong>{' '}
                                            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer"
                                               className="text-[#7B61FF] hover:text-[#8A6DFF] underline">
                                                Google Privacy Policy
                                            </a>
                                        </Text>
                                    </div>
                                    <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                                        <Text variant="secondary" className="leading-relaxed mb-2">
                                            <strong className="text-white">Paddle (Payments)</strong>
                                        </Text>
                                        <Text variant="secondary" className="leading-relaxed text-sm">
                                            <strong>Purpose:</strong> Secure payment processing for subscriptions and purchases. Paddle acts as our Merchant of Record.<br/>
                                            <strong>Data collected:</strong> Payment details, billing address (handled by Paddle, not stored by us).<br/>
                                            <strong>Legal basis:</strong> Contract performance (necessary to process your payment).<br/>
                                            <strong>Privacy policy:</strong>{' '}
                                            <a href="https://www.paddle.com/legal/privacy" target="_blank" rel="noopener noreferrer"
                                               className="text-[#7B61FF] hover:text-[#8A6DFF] underline">
                                                Paddle Privacy Policy
                                            </a>
                                        </Text>
                                    </div>
                                    <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                                        <Text variant="secondary" className="leading-relaxed mb-2">
                                            <strong className="text-white">Cloudflare (Security & Performance)</strong>
                                        </Text>
                                        <Text variant="secondary" className="leading-relaxed text-sm mb-3">
                                            <strong>Purpose:</strong> DDoS protection, CDN delivery, and security verification.<br/>
                                            <strong>Data collected:</strong> IP address, browser type (for security analysis).<br/>
                                            <strong>Legal basis:</strong> Legitimate interest (protecting our service from attacks).<br/>
                                            <strong>Privacy policy:</strong>{' '}
                                            <a href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noopener noreferrer"
                                               className="text-[#7B61FF] hover:text-[#8A6DFF] underline">
                                                Cloudflare Privacy Policy
                                            </a>
                                        </Text>
                                        <Text variant="secondary" className="leading-relaxed text-sm">
                                            <strong>Cookies set by Cloudflare:</strong>
                                        </Text>
                                        <ul className="list-disc list-inside space-y-1 text-gray-300 text-xs mt-2">
                                            <li><span className="font-mono text-blue-400">__cf_bm</span> - Bot management cookie for security verification (30 min)</li>
                                            <li><span className="font-mono text-blue-400">cf_clearance</span> - Challenge verification cookie after security check (30 min)</li>
                                        </ul>
                                    </div>
                                </div>
                            </section>

                            {/* Managing Cookies */}
                            <section>
                                <Heading level={3} className="mb-4 text-[#BA87FF]">4. Managing Cookies</Heading>
                                <Text variant="secondary" className="leading-relaxed mb-4">
                                    You can manage cookies through your browser settings. Here's how:
                                </Text>
                                <ul className="list-disc list-inside space-y-2 text-gray-300">
                                    <li><strong className="text-white">Chrome:</strong> Settings → Privacy and security → Cookies</li>
                                    <li><strong className="text-white">Firefox:</strong> Settings → Privacy & Security → Cookies</li>
                                    <li><strong className="text-white">Safari:</strong> Preferences → Privacy → Cookies</li>
                                    <li><strong className="text-white">Edge:</strong> Settings → Cookies and site permissions</li>
                                </ul>
                                <Text variant="secondary" className="leading-relaxed mt-4">
                                    <strong className="text-yellow-400">Note:</strong> Disabling essential cookies may prevent
                                    you from using certain features of our Service, including signing in.
                                </Text>
                            </section>

                            {/* Do Not Track */}
                            <section>
                                <Heading level={3} className="mb-4 text-[#BA87FF]">5. Do Not Track Signals</Heading>
                                <Text variant="secondary" className="leading-relaxed">
                                    Some browsers have a "Do Not Track" feature that signals to websites that you do not
                                    want to have your online activity tracked. We honor Do Not Track signals and do not
                                    track, plant cookies, or use advertising when a Do Not Track browser mechanism is in place.
                                </Text>
                            </section>

                            {/* Updates */}
                            <section>
                                <Heading level={3} className="mb-4 text-[#BA87FF]">6. Updates to This Policy</Heading>
                                <Text variant="secondary" className="leading-relaxed">
                                    We may update this Cookie Policy from time to time to reflect changes in our practices
                                    or for other operational, legal, or regulatory reasons. We will notify you of any
                                    material changes by updating the "Last updated" date at the top of this policy.
                                </Text>
                            </section>

                            {/* Contact */}
                            <section>
                                <Heading level={3} className="mb-4 text-[#BA87FF]">7. Contact Us</Heading>
                                <Text variant="secondary" className="leading-relaxed mb-4">
                                    If you have any questions about our use of cookies or wish to exercise your rights,
                                    please contact us through our contact page:
                                </Text>
                                <Button
                                    onClick={() => navigate('/contact')}
                                    variant="ghost"
                                    className="text-[#7B61FF] hover:text-[#8A6DFF] underline p-0"
                                >
                                    Contact Us
                                </Button>
                            </section>

                            {/* Back Button */}
                            <div className="pt-8 border-t border-white/10 text-center">
                                <Button
                                    onClick={() => navigate(-1)}
                                    variant="ghost"
                                    className="flex items-center mx-auto"
                                >
                                    <Icon name="arrowLeft" size="xs" className="mr-2" />
                                    Go Back
                                </Button>
                            </div>
                        </div>
                    </Container>
                </main>

                <Footer />
            </div>
        </div>
    );
}
